import { ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { useModal } from 'xstate/hooks/useModal';
import { BoxModalPayload, BoxModalStep } from 'xstate/machines/ModalMachine';
import { closeModal, openBoxModal } from 'xstate/actors/ModalActor';
import MemoryBoxForm from 'modules/MemoryBoxForm';
import ManageAccessForm from 'modules/ManageAccessForm';
import InvitePeopleForm from 'modules/InvitePeopleForm';
import SharingSettingsForm from 'modules/SharingSettingsForm';
import BoxAppearanceForm from 'modules/BoxAppearanceForm';
import BoxSettingsForm from 'modules/BoxSettingsForm';
import { useTranslations } from 'next-intl';

type Header = { title: string; subtitle?: string; onBack?: () => void };

const BoxModal = (): ReactElement => {
  const t = useTranslations();
  const { payload } = useModal() as { payload: BoxModalPayload };
  const box = payload?.box;
  const step = payload?.step ?? BoxModalStep.CreateOrEdit;
  const canNavigateBack = payload?.canNavigateBack ?? true;

  const { header, body }: { header: Header; body: ReactNode } = (() => {
    switch (step) {
      case BoxModalStep.Appearance:
        return {
          header: { title: t('boxes.menu.appearance') },
          body: box ? <BoxAppearanceForm box={box} /> : null,
        };
      case BoxModalStep.ManagePermissions:
        return {
          header: {
            title: t('boxes.menu.manageAccess'),
            subtitle: box?.name,
            onBack: canNavigateBack
              ? () =>
                  openBoxModal(box, {
                    step: BoxModalStep.CreateOrEdit,
                    canNavigateBack,
                  })
              : undefined,
          },
          body: box ? <ManageAccessForm box={box} /> : null,
        };
      case BoxModalStep.InvitePeople:
        return {
          header: {
            title: t('boxSharing.inviteByEmail'),
            onBack: canNavigateBack
              ? () =>
                  openBoxModal(box, {
                    step: BoxModalStep.ManagePermissions,
                    canNavigateBack,
                  })
              : undefined,
          },
          body: box ? <InvitePeopleForm box={box} /> : null,
        };
      case BoxModalStep.Settings:
        return {
          header: { title: t('boxSettings.title') },
          body: box ? <BoxSettingsForm box={box} step={step} /> : null,
        };
      case BoxModalStep.SettingsAccess:
      case BoxModalStep.SettingsDanger:
        return {
          header: {
            title:
              step === BoxModalStep.SettingsAccess
                ? t('boxSettings.accessEditorTitle')
                : t('boxSettings.dangerTitle'),
            onBack: () => openBoxModal(box, { step: BoxModalStep.Settings }),
          },
          body: box ? <BoxSettingsForm box={box} step={step} /> : null,
        };
      case BoxModalStep.SharingSettings:
        return {
          header: { title: box?.name ?? t('boxes.menu.sharingSettings') },
          body: box ? <SharingSettingsForm box={box} /> : null,
        };
      case BoxModalStep.CreateOrEdit:
      default:
        return {
          header: {
            title: box
              ? t('boxes.form.titleEdit')
              : t('boxes.form.titleCreate'),
          },
          body: <MemoryBoxForm box={box} />,
        };
    }
  })();

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <div className={styles.header.titleWrap}>
              {header.onBack && (
                <button
                  type="button"
                  onClick={header.onBack}
                  aria-label={t('common.back')}
                  className={styles.header.back}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}
              <div>
                <DialogTitle as="h2" className={styles.header.title}>
                  {header.title}
                </DialogTitle>
                {header.subtitle && (
                  <p className={styles.header.subtitle}>{header.subtitle}</p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={closeModal}
              aria-label={t('common.close')}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>{body}</div>
        </DialogPanel>
      </div>
    </div>
  );
};

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-4',
  panel: classNames(
    'w-full max-w-xl rounded-2xl bg-white text-left shadow-xl transition-all',
    'data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  header: {
    container: 'flex items-start justify-between gap-3 px-6 pt-6 pb-4',
    titleWrap: 'flex items-start gap-2 min-w-0',
    back: 'flex-none mt-0.5 text-gray-400 hover:text-gray-600',
    title: 'text-lg font-bold text-mf-off-black-950',
    subtitle: 'text-sm text-gray-500',
    close:
      'flex-none flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200',
  },
  body: 'pb-6',
};

export default BoxModal;
